/**
 * Service editor — Delivery section. Verbatim port of screens-service-delivery.jsx.
 * A service can offer several delivery methods; each gets its own tab with full
 * per-method settings: timing, method-specific options, pricing, weekly
 * schedule, booking questions, coupons/promos and a communications plan.
 * Also exports the shared ExceptionList (uniform staff/resource/service shape).
 */
import { useEffect, useState, type ReactNode } from "react";
import {
  Banner, Btn, Card, Dropdown, Field, Icon, IconTile, InlineEmptyState, Input,
  MediaRow, Pill, SearchPickerDialog, SectionLabel, SelectableTile, Skeleton,
  StatusPill, T, Tab, Tabs, Toggle,
} from "@v0y4ge/jrni-ui";
import { toast } from "../../shell/toast";
import { useQuestions } from "../questions/useQuestions";
import { useDiscounts } from "../promos/useDiscounts";
import { useFlows, FLOW_KIND_PILL } from "../messaging/useFlows";
import {
  SVC_METHODS, SVC_METHOD, SVC_WEEKDAYS, PAYMENT_TIMINGS, svcDefaultMethodConfig,
  type Service, type SvcMethodConfig,
} from "./services-data";
import { SvcRow, SvcSeg, SvcSelect, SvcNumField, SvcSectionHead } from "./service-editor-controls";
import { useT } from "../../lib/i18n";

const IconMap = Icon as unknown as Record<string, (p?: { size?: number }) => ReactNode>;

let _exSeq = 0;
/** Uniform exception shape (staff timeOff / resource downtime / service exceptions). */
export function normalizeException(x: Record<string, any>): { id: string; kind: string; label: string; from: string; to: string } {
  return { id: x.id || "ex_" + (Date.now().toString(36) + (_exSeq++).toString(36)), kind: x.kind === "extra" ? "extra" : "block", label: x.label || "", from: x.from || "", to: x.to || "" };
}

export function ServiceDeliverySection({ form, set, setMethodCfg, canWrite }: {
  form: Service; set: (p: Partial<Service>) => void; setMethodCfg: (methodId: string, patch: Record<string, any>) => void; canWrite: boolean;
}) {
  const t = useT();
  const [active, setActive] = useState<string | null>(form.methods[0] || null);
  useEffect(() => { if (!form.methods.includes(active)) setActive(form.methods[0] || null); }, [form.methods.join(",")]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleMethod = (id: string) => {
    const on = form.methods.includes(id);
    if (on) { set({ methods: form.methods.filter((m: string) => m !== id) }); }
    else {
      const cfg = form.methodConfig[id] || svcDefaultMethodConfig(id, form);
      set({ methods: [...form.methods, id], methodConfig: { ...form.methodConfig, [id]: cfg } });
      setActive(id);
    }
  };

  return (
    <div>
      <SvcSectionHead icon={<Icon.layers size={18} />} title={t("services.delivery.deliveryMethods", "Delivery methods")} desc={t("services.delivery.desc", "How the service is delivered. Enable any combination — each method has its own settings.")} />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))", gap: T.space[3], padding: `${T.space[4]} 0`, borderTop: `1px solid ${T.border}` }}>
        {SVC_METHODS.map((m) => (
          <SelectableTile
            key={m.id}
            multiple
            size="sm"
            selected={form.methods.includes(m.id)}
            onSelect={() => toggleMethod(m.id)}
            disabled={!canWrite}
            leading={<IconTile color={m.hue} icon={IconMap[m.icon] ? IconMap[m.icon]({ size: 16 }) : null} />}
            title={m.label}
            description={m.sub}
          />
        ))}
      </div>

      {form.methods.length === 0 ? (
        <InlineEmptyState
          style={{ marginTop: T.space[2] }}
          icon={<IconTile size="lg" icon={<Icon.layers size={20} />} />}
          title={t("services.delivery.enableADeliveryMethodToConfigureIt", "Enable a delivery method to configure it")}
        />
      ) : (
        <div style={{ marginTop: T.space[2] }}>
          <Tabs value={active ?? ""} onChange={(v) => setActive(String(v))} style={{ overflowX: "auto" }}>
            {form.methods.map((id: string) => {
              const m = SVC_METHOD(id);
              return (
                <Tab key={id} value={id} style={{ flexShrink: 0 }} icon={IconMap[m.icon] ? IconMap[m.icon]({ size: 15 }) : null}>{m.label}</Tab>
              );
            })}
          </Tabs>
          {active ? <DeliveryMethodPanel key={active} methodId={active} cfg={form.methodConfig[active]} setCfg={(patch) => setMethodCfg(active, patch)} canWrite={canWrite} /> : null}
        </div>
      )}
    </div>
  );
}

/* ── One delivery method's settings ──────────────────────────────────────── */
function DeliveryMethodPanel({ methodId, cfg, setCfg, canWrite }: { methodId: string; cfg: SvcMethodConfig; setCfg: (p: Record<string, any>) => void; canWrite: boolean }) {
  const t = useT();
  const setPricing = (patch: Record<string, any>) => setCfg({ pricing: { ...cfg.pricing, ...patch } });
  const setComms = (patch: Record<string, any>) => setCfg({ comms: { ...cfg.comms, ...patch } });
  return (
    <div>
      <SvcRow label={t("services.delivery.timing", "Timing")} hint={t("services.delivery.hint", "Service length plus prep before and cleanup after.")}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: T.space[4] }}>
          <LblCol label={t("services.delivery.duration", "Duration")}><SvcNumField value={cfg.duration} onChange={(v) => setCfg({ duration: v })} min={5} max={1440} step={5} unit="min" disabled={!canWrite} /></LblCol>
          <LblCol label={t("services.delivery.preTime", "Pre-time")}><SvcNumField value={cfg.pre} onChange={(v) => setCfg({ pre: v })} min={0} max={240} step={5} unit="min" disabled={!canWrite} /></LblCol>
          <LblCol label={t("services.delivery.postTime", "Post-time")}><SvcNumField value={cfg.post} onChange={(v) => setCfg({ post: v })} min={0} max={240} step={5} unit="min" disabled={!canWrite} /></LblCol>
          <LblCol label={t("services.delivery.capacity", "Capacity")}><SvcNumField value={cfg.capacity} onChange={(v) => setCfg({ capacity: v })} min={1} max={1000} unit={cfg.capacity === 1 ? "person" : "people"} width={130} disabled={!canWrite} /></LblCol>
        </div>
      </SvcRow>

      <MethodSpecific methodId={methodId} cfg={cfg} setCfg={setCfg} canWrite={canWrite} />

      <SvcRow label={t("services.delivery.pricing", "Pricing")} hint={t("services.delivery.hint2", "Per-booking charges for this delivery method.")}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: T.space[4] }}>
          <LblCol label={t("services.delivery.price", "Price")}><SvcNumField value={cfg.pricing.price} onChange={(v) => setPricing({ price: v })} prefix="$" min={0} step={5} width={120} disabled={!canWrite} /></LblCol>
          <LblCol label={t("services.delivery.deposit", "Deposit")}><SvcNumField value={cfg.pricing.deposit} onChange={(v) => setPricing({ deposit: v })} prefix="$" min={0} step={5} width={120} disabled={!canWrite} /></LblCol>
          <LblCol label={t("services.delivery.tax", "Tax")}><SvcNumField value={cfg.pricing.tax} onChange={(v) => setPricing({ tax: v })} unit="%" min={0} max={100} width={100} disabled={!canWrite} /></LblCol>
          <LblCol label={t("services.delivery.cancellationFee", "Cancellation fee")}><SvcNumField value={cfg.pricing.cancelFee} onChange={(v) => setPricing({ cancelFee: v })} prefix="$" min={0} step={5} width={120} disabled={!canWrite} /></LblCol>
          <LblCol label={t("services.delivery.noShowFee", "No-show fee")}><SvcNumField value={cfg.pricing.noShowFee} onChange={(v) => setPricing({ noShowFee: v })} prefix="$" min={0} step={5} width={120} disabled={!canWrite} /></LblCol>
          <LblCol label={t("services.delivery.paymentTiming", "Payment timing")}><div style={{ width: 160 }}><SvcSelect value={cfg.pricing.paymentTiming} onChange={(v) => setPricing({ paymentTiming: v })} disabled={!canWrite} options={PAYMENT_TIMINGS} /></div></LblCol>
        </div>
      </SvcRow>

      <SvcRow label={t("services.delivery.availability", "Availability")} hint={t("services.delivery.hint3", "Weekly times this method can be booked.")} full>
        <ScheduleGrid schedule={cfg.schedule} onChange={(wk) => setCfg({ schedule: wk })} canWrite={canWrite} />
        <Banner tone="info" size="compact" style={{ marginTop: T.space[3] }}>
          {t("services.delivery.thisIsThe", "This is the")}{" "}<b>{t("services.delivery.serviceLevel", "service-level")}</b> {t("services.delivery.availabilityABookableSlotIsThe", "availability. A bookable slot is the")}{" "}<b>{t("services.delivery.intersection", "intersection")}</b> {t("services.delivery.ofThisScheduleTheAssigned", "of this schedule, the assigned")}{" "}<b>{t("services.delivery.staffMemberS", "staff member's")}</b> {t("services.delivery.scheduleAny", "schedule, any")}{" "}<b>{t("services.delivery.requiredResourceS", "required resource's")}</b> {t("services.delivery.scheduleAndThe", "schedule, and the")}{" "}<b>{t("services.delivery.locationS", "location's")}</b> {t("services.delivery.openingHours", "opening hours.")}
        </Banner>
      </SvcRow>

      <MethodQuestions cfg={cfg} setCfg={setCfg} canWrite={canWrite} />
      <MethodPromos cfg={cfg} setCfg={setCfg} canWrite={canWrite} />
      <MethodComms cfg={cfg} setComms={setComms} canWrite={canWrite} />
    </div>
  );
}

/** Overline micro-label above a control — the DS `Field` shell with the
 *  `SectionLabel` overline in its label slot (this screen's dense inline
 *  clusters read as tracked overlines, not as `Field`'s sentence-case labels). */
function LblCol({ label, children }: { label: string; children: ReactNode }) {
  return <Field label={<SectionLabel as="span">{label}</SectionLabel>}>{children}</Field>;
}

/** The three library collections this section reads (questions / discounts /
 *  flows) all hydrate asynchronously and every one of them exposes `loading`
 *  and an error — which this screen used to ignore, rendering its EMPTY state
 *  over both (`useDiscounts` says so in its own doc comment: "never render an
 *  empty list over this"). One shared treatment so sibling surfaces match:
 *  loading → two `Skeleton` rows · failed read → a danger `Banner` (plus Retry
 *  wherever the store exposes one) · genuinely empty → `InlineEmptyState`.
 *  Copy is the existing generic catalog keys — no new strings. */
function CollectionState({ loading, error, onRetry, children }: {
  loading?: boolean; error?: unknown; onRetry?: () => void; children: ReactNode;
}) {
  const t = useT();
  if (loading) return (
    <div aria-busy="true" aria-label={t("common.status.loading", "Loading…")} style={{ display: "flex", flexDirection: "column", gap: T.space[2] }}>
      <Skeleton height={44} /><Skeleton height={44} />
    </div>
  );
  if (error) return (
    <Banner
      tone="danger"
      size="compact"
      title={t("common.status.error", "Something went wrong")}
      action={onRetry ? <Btn variant="secondary" size="sm" onClick={onRetry}>{t("common.actions.retry", "Retry")}</Btn> : undefined}
    />
  );
  return <>{children}</>;
}

/* method-specific settings + the drive-time callout for in-home */
function MethodSpecific({ methodId, cfg, setCfg, canWrite }: { methodId: string; cfg: SvcMethodConfig; setCfg: (p: Record<string, any>) => void; canWrite: boolean }) {
  const t = useT();
  if (methodId === "in-person") return (
    <SvcRow label={t("services.delivery.locationDetails", "Location details")} hint={t("services.delivery.hint4", "Where customers come to.")}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: T.space[4] }}>
        <LblCol label={t("services.delivery.roomStation", "Room / station")}><Input value={cfg.room || ""} onChange={(e) => setCfg({ room: e.target.value })} placeholder={t("services.delivery.eGConsultingRoom2", "e.g. Consulting room 2")} disabled={!canWrite} style={{ width: 240 }} /></LblCol>
      </div>
    </SvcRow>
  );
  if (methodId === "online") return (
    <SvcRow label={t("services.delivery.onlineMeeting", "Online meeting")} hint={t("services.delivery.hint5", "How the video session is created.")}>
      <div style={{ display: "flex", flexDirection: "column", gap: T.space[4] }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: T.space[4], alignItems: "flex-end" }}>
          <LblCol label={t("services.delivery.provider", "Provider")}><div style={{ width: 180 }}><SvcSelect value={cfg.provider} onChange={(v) => setCfg({ provider: v })} disabled={!canWrite} options={[{ id: "zoom", label: t("services.delivery.zoom", "Zoom") }, { id: "teams", label: t("services.delivery.microsoftTeams", "Microsoft Teams") }, { id: "meet", label: t("services.delivery.googleMeet", "Google Meet") }, { id: "webex", label: t("services.delivery.webex", "Webex") }]} /></div></LblCol>
          <label style={{ display: "inline-flex", alignItems: "center", gap: T.space[2], height: 40 }}><Toggle on={cfg.autoLink} onChange={canWrite ? (v: boolean) => setCfg({ autoLink: v }) : undefined} /><span style={{ ...T.text.body, color: T.n2 }}>{t("services.delivery.autoGenerateAJoinLinkPerBooking", "Auto-generate a join link per booking")}</span></label>
        </div>
        <LblCol label={t("services.delivery.joinInstructions", "Join instructions")}><Input value={cfg.joinInstructions || ""} onChange={(e) => setCfg({ joinInstructions: e.target.value })} placeholder={t("services.delivery.shownWithTheJoinLink", "Shown with the join link")} disabled={!canWrite} /></LblCol>
      </div>
    </SvcRow>
  );
  if (methodId === "phone") return (
    <SvcRow label={t("services.delivery.phoneCall", "Phone call")} hint={t("services.delivery.hint6", "Who places the call.")}>
      <SvcSeg value={cfg.whoCalls} onChange={(v) => setCfg({ whoCalls: v })} disabled={!canWrite} options={[{ id: "staff", label: t("services.delivery.staffCallsCustomer", "Staff calls customer") }, { id: "customer", label: t("services.delivery.customerCallsIn", "Customer calls in") }]} />
    </SvcRow>
  );
  if (methodId === "email") return (
    <SvcRow label={t("services.delivery.emailService", "Email service")} hint={t("services.delivery.hint7", "Asynchronous fulfilment details.")}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: T.space[4] }}>
        <LblCol label={t("services.delivery.turnaroundSla", "Turnaround / SLA")}><Input value={cfg.sla || ""} onChange={(e) => setCfg({ sla: e.target.value })} placeholder={t("services.delivery.eG1BusinessDay", "e.g. 1 business day")} disabled={!canWrite} style={{ width: 200 }} /></LblCol>
        <LblCol label={t("services.delivery.deliverable", "Deliverable")}><Input value={cfg.deliverable || ""} onChange={(e) => setCfg({ deliverable: e.target.value })} placeholder={t("services.delivery.whatTheCustomerReceives", "What the customer receives")} disabled={!canWrite} style={{ width: 260 }} /></LblCol>
      </div>
    </SvcRow>
  );
  if (methodId === "customer-location") return (
    <SvcRow label={t("services.delivery.travel", "Travel")} hint={t("services.delivery.hint8", "Staff travels to the customer — account for time and distance.")}>
      <div style={{ display: "flex", flexDirection: "column", gap: T.space[3] }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: T.space[4] }}>
          <LblCol label={t("services.delivery.driveTime", "Drive time")}><SvcNumField value={cfg.driveTime} onChange={(v) => setCfg({ driveTime: v })} min={0} max={480} step={5} unit="min" disabled={!canWrite} /></LblCol>
          <LblCol label={t("services.delivery.travelRadius", "Travel radius")}><SvcNumField value={cfg.travelRadius} onChange={(v) => setCfg({ travelRadius: v })} min={0} max={500} unit="mi" disabled={!canWrite} /></LblCol>
          <LblCol label={t("services.delivery.travelFee", "Travel fee")}><SvcNumField value={cfg.travelFee} onChange={(v) => setCfg({ travelFee: v })} prefix="$" min={0} step={5} width={120} disabled={!canWrite} /></LblCol>
          <label style={{ display: "inline-flex", alignItems: "center", gap: T.space[2], height: 40, alignSelf: "flex-end" }}><Toggle on={cfg.captureAddress} onChange={canWrite ? (v: boolean) => setCfg({ captureAddress: v }) : undefined} /><span style={{ ...T.text.body, color: T.n2 }}>{t("services.delivery.captureAddressAtBooking", "Capture address at booking")}</span></label>
        </div>
        <Banner tone="info" size="compact">
          {t("services.delivery.driveTimeIsReserved", "Drive time is reserved")}{" "}<b>{t("services.delivery.around", "around")}</b> {t("services.delivery.theBookingOnTopOfPrePostAndDurationSoTheSame", "the booking (on top of pre/post and duration), so the same service costs more time at a customer's location than in a store.")}
        </Banner>
      </div>
    </SvcRow>
  );
  return null;
}

/* weekly availability grid */
export function ScheduleGrid({ schedule, onChange, canWrite }: { schedule: Record<string, { start: string; end: string }[]>; onChange: (wk: Record<string, { start: string; end: string }[]>) => void; canWrite: boolean }) {
  const t = useT();
  const setDay = (day: string, ranges: { start: string; end: string }[]) => onChange({ ...schedule, [day]: ranges });
  const addRange = (day: string) => setDay(day, [...(schedule[day] || []), { start: "09:00", end: "17:00" }]);
  const rmRange = (day: string, i: number) => setDay(day, schedule[day].filter((_, idx) => idx !== i));
  const setRange = (day: string, i: number, patch: Partial<{ start: string; end: string }>) => setDay(day, schedule[day].map((r, idx) => idx === i ? { ...r, ...patch } : r));
  return (
    <Card padding="none" style={{ overflow: "hidden", marginTop: T.space[1] }}>
      {SVC_WEEKDAYS.map((day, di) => {
        const ranges = schedule[day] || [];
        const off = ranges.length === 0;
        return (
          <div key={day} style={{ display: "flex", alignItems: "flex-start", gap: T.space[3], padding: `${T.space[3]} ${T.space[4]}`, borderTop: di ? `1px solid ${T.border}` : "none", background: off ? T.canvas : T.white }}>
            <div style={{ width: 44, ...T.text.bodySm, fontWeight: 700, color: off ? T.soft : T.n1, paddingTop: T.space[2] }}>{day}</div>
            <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: T.space[2] }}>
              {off ? <span style={{ ...T.text.bodySm, color: T.soft, paddingTop: T.space[2] }}>{t("services.delivery.unavailable", "Unavailable")}</span> : ranges.map((r, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: T.space[2] }}>
                  <Input type="time" value={r.start} disabled={!canWrite} onChange={(e) => setRange(day, i, { start: e.target.value })} full={false} size="sm" style={{ width: "auto" }} />
                  <span style={{ color: T.soft, ...T.text.bodySm }}>–</span>
                  <Input type="time" value={r.end} disabled={!canWrite} onChange={(e) => setRange(day, i, { end: e.target.value })} full={false} size="sm" style={{ width: "auto" }} />
                  {canWrite ? <Btn variant="ghost" size="sm" iconOnly={<Icon.x size={14} />} onClick={() => rmRange(day, i)} aria-label={t("common.actions.remove", "Remove")} title={t("common.actions.remove", "Remove")} /> : null}
                </div>
              ))}
            </div>
            {canWrite ? <Btn variant="secondary" size="sm" icon={<Icon.plus size={12} />} onClick={() => addRange(day)}>{t("services.delivery.time", "Time")}</Btn> : null}
          </div>
        );
      })}
    </Card>
  );
}

/* Shared date-exception editor (S-3 / S-4) — one shape across staff (timeOff),
 * resources (downtime) and services (exceptions). With allowExtra, a row can
 * ADD availability outside the weekly grid, not only block it.
 *
 * NOTE (2026-08-10 DS pass): jrni-ui now ships its OWN `ExceptionList`, so this
 * is a duplicate surface for the same problem. It is NOT retired yet because the
 * DS component cannot express three things this one's consumers depend on — a
 * read-only (`canWrite: false`) mode, localizable column headers, and the
 * block/extra tone stripe. Filed as a proposal rather than forced; see the
 * migration report. Do not fork a THIRD one.
 */
export function ExceptionList({ items, onChange, canWrite, allowExtra = true, blockOnly = false }: {
  items: Record<string, any>[]; onChange: (next: Record<string, any>[]) => void; canWrite: boolean; allowExtra?: boolean; blockOnly?: boolean;
}) {
  const t = useT();
  const translate = t;
  const list = (items || []).map(normalizeException);
  const setRow = (i: number, patch: Record<string, any>) => onChange(list.map((t, idx) => idx === i ? { ...t, ...patch } : t));
  const rm = (i: number) => onChange(list.filter((_, idx) => idx !== i));
  const add = () => onChange([...list, normalizeException({ kind: "block" })]);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: T.space[2], marginTop: T.space[1] }}>
      {list.length === 0 ? <InlineEmptyState size="sm" title={t("services.delivery.noExceptions", "No exceptions.")} /> : list.map((t, i) => (
        <Card
          key={t.id || i}
          style={{
            display: "flex", flexWrap: "wrap", alignItems: "center", gap: T.space[2],
            padding: `${T.space[2]} ${T.space[3]}`,
            borderColor: t.kind === "extra" ? T.green2 : T.border,
            borderLeft: `3px solid ${t.kind === "extra" ? T.green1 : T.orange1}`,
          }}
        >
          {allowExtra && !blockOnly ? (
            <Dropdown
              value={t.kind}
              disabled={!canWrite}
              onChange={(v) => setRow(i, { kind: v })}
              aria-label={translate("services.delivery.blockOff", "Block off")}
              options={[
                { value: "block", label: <span style={{ fontWeight: 600, color: T.orange1 }}>{translate("services.delivery.blockOff", "Block off")}</span> },
                { value: "extra", label: <span style={{ fontWeight: 600, color: T.green1 }}>{translate("services.delivery.extraAvailability", "Extra availability")}</span> },
              ]}
              style={{ width: 172 }}
            />
          ) : null}
          <Input value={t.label} onChange={(e) => setRow(i, { label: e.target.value })} disabled={!canWrite} placeholder={t.kind === "extra" ? translate("services.serviceDelivery.reasonEGPopUpSaturday", "Reason (e.g. Pop-up Saturday)") : translate("services.serviceDelivery.reasonEGVacation", "Reason (e.g. Vacation)")} full={false} style={{ flex: 1, minWidth: 140 }} />
          <Input type="date" value={t.from} onChange={(e) => setRow(i, { from: e.target.value })} disabled={!canWrite} full={false} />
          <span style={{ color: T.soft, ...T.text.bodySm }}>→</span>
          <Input type="date" value={t.to} onChange={(e) => setRow(i, { to: e.target.value })} disabled={!canWrite} full={false} />
          {t.from && !t.to ? <span title={translate("services.delivery.openEndedNoEndDate", "Open-ended — no end date")}><StatusPill tone="warning" size="sm">{translate("services.delivery.ongoing", "⚠ ongoing")}</StatusPill></span> : null}
          {canWrite ? <Btn variant="ghost" size="sm" iconOnly={<Icon.x size={14} />} onClick={() => rm(i)} aria-label={translate("common.actions.remove", "Remove")} title={translate("common.actions.remove", "Remove")} /> : null}
        </Card>
      ))}
      {canWrite ? <div><Btn variant="secondary" size="sm" icon={<Icon.plus size={13} />} onClick={add}>{t("services.delivery.addException", "Add exception")}</Btn></div> : null}
    </div>
  );
}

/* booking questions per method */
function MethodQuestions({ cfg, setCfg, canWrite }: { cfg: SvcMethodConfig; setCfg: (p: Record<string, any>) => void; canWrite: boolean }) {
  const t = useT();
  const [picking, setPicking] = useState(false);
  const qstore = useQuestions();
  const items: { kind: string; id: string }[] = cfg.questions || [];
  const resolve = (it: { kind: string; id: string }) =>
    it.kind === "group" ? (qstore.groups().find((g: any) => g.id === it.id) || {}) : (qstore.questions().find((q: any) => q.id === it.id) || {});
  const add = (kind: string, id: string) => { if (!items.some((x) => x.kind === kind && x.id === id)) setCfg({ questions: [...items, { kind, id }] }); };
  const rm = (idx: number) => setCfg({ questions: items.filter((_, i) => i !== idx) });
  const taken = new Set(items.map((x) => x.kind + x.id));
  const gs = qstore.groups().filter((g: any) => !taken.has("group" + g.id));
  const qs = qstore.questions().filter((q: any) => !taken.has("question" + q.id));
  // SearchPickerDialog owns the search box + filtering, so ids are namespaced
  // ("g"/"q") to keep one flat pickable list across both kinds.
  const pickable = [
    ...gs.map((g: any) => ({
      id: "g" + g.id, title: g.name,
      subtitle: <>{t("services.delivery.questionGroup", "Question group ·")}{t("services.delivery.questionCount.icu", "{count, plural, one {# question} other {# questions}}", { count: (g.items || []).length })}</>,
      leading: <IconTile tone="primary" icon={<Icon.layers size={15} />} />,
      onPick: () => add("group", g.id),
    })),
    ...qs.map((q: any) => ({
      id: "q" + q.id, title: q.label,
      subtitle: t("services.delivery.question", "Question"),
      leading: <IconTile icon={<Icon.help size={15} />} />,
      onPick: () => add("question", q.id),
    })),
  ];
  return (
    <SvcRow label={t("services.delivery.bookingQuestions", "Booking questions")} hint={t("services.delivery.hint9", "Information collected at booking for this method.")} full>
      <div style={{ display: "flex", flexDirection: "column", gap: T.space[2], marginTop: T.space[1] }}>
        {items.length === 0 ? <InlineEmptyState size="sm" title={t("services.delivery.noQuestionsYet", "No questions yet.")} /> : items.map((it, i) => {
          const obj: any = resolve(it);
          return (
            <Card key={it.kind + it.id}>
              <MediaRow
                leading={<IconTile size="sm" tone={it.kind === "group" ? "primary" : "neutral"} icon={it.kind === "group" ? <Icon.layers size={13} /> : <Icon.help size={13} />} />}
                title={obj.name || obj.label || "—"}
                trailing={<>
                  {it.kind === "group" ? <Pill tone="primary">{t("services.delivery.group", "Group")}</Pill> : <Pill tone="neutral">{t("services.delivery.question", "Question")}</Pill>}
                  {canWrite ? <Btn variant="ghost" size="sm" iconOnly={<Icon.x size={14} />} onClick={() => rm(i)} aria-label={t("common.actions.remove", "Remove")} title={t("common.actions.remove", "Remove")} /> : null}
                </>}
              />
            </Card>
          );
        })}
        {canWrite ? <div><Btn variant="secondary" size="sm" icon={<Icon.plus size={13} />} onClick={() => setPicking(true)}>{t("services.delivery.addQuestions", "Add questions")}</Btn></div> : null}
      </div>
      <SearchPickerDialog
        open={picking}
        onClose={() => setPicking(false)}
        title={t("services.delivery.addBookingQuestions", "Add booking questions")}
        searchPlaceholder={t("services.delivery.searchQuestionsGroups", "Search questions & groups…")}
        items={qstore.loading || qstore.loadError() ? [] : pickable}
        emptyState={
          <div style={{ margin: T.space[4] }}>
            <CollectionState loading={qstore.loading} error={qstore.loadError()} onRetry={qstore.retryLoad}>
              <InlineEmptyState style={{ border: "none" }} icon={<IconTile size="lg" icon={<Icon.search size={20} />} />} title={t("services.delivery.noQuestionsMatch", "No questions match.")} />
            </CollectionState>
          </div>
        }
        onPick={(id) => { pickable.find((p) => p.id === id)?.onPick(); setPicking(false); }}
      />
    </SvcRow>
  );
}

/* coupons & promos per method */
function MethodPromos({ cfg, setCfg, canWrite }: { cfg: SvcMethodConfig; setCfg: (p: Record<string, any>) => void; canWrite: boolean }) {
  const t = useT();
  const [picking, setPicking] = useState(false);
  const dstore = useDiscounts();
  const ids: string[] = cfg.promos || [];
  const add = (id: string) => { if (!ids.includes(id)) setCfg({ promos: [...ids, id] }); };
  const rm = (id: string) => setCfg({ promos: ids.filter((x) => x !== id) });
  const pickable = dstore.all().filter((d) => !ids.includes(d.id)).map((d) => ({
    id: d.id, title: d.name,
    subtitle: d.type === "coupon" ? t("services.serviceDelivery.couponDCode", "Coupon · {value0}", { value0: d.code }) : t("services.serviceDelivery.automaticPromo", "Automatic promo"),
    leading: <IconTile tone="primary" icon={<Icon.tags size={15} />} />,
  }));
  return (
    <SvcRow label={t("services.delivery.couponsPromos", "Coupons & promos")} hint={t("services.delivery.hint10", "Discounts that apply to this method.")} full>
      <div style={{ display: "flex", flexDirection: "column", gap: T.space[2], marginTop: T.space[1] }}>
        {ids.length === 0 ? <InlineEmptyState size="sm" title={t("services.delivery.noDiscountsAttached", "No discounts attached.")} /> : ids.map((id) => {
          const d = dstore.get(id);
          return (
            <Card key={id}>
              <MediaRow
                leading={<IconTile size="sm" tone="primary" icon={<Icon.tags size={13} />} />}
                // Names come from the discounts store; while it hydrates the row
                // used to print the raw id ("d1"). Loading copy, never the id.
                title={d ? d.name : dstore.loading ? t("common.status.loading", "Loading…") : id}
                trailing={<>
                  {d ? <Pill tone="neutral">{d.type === "coupon" ? t("services.delivery.text", "Coupon") : t("services.delivery.text2", "Promo")}</Pill> : null}
                  {canWrite ? <Btn variant="ghost" size="sm" iconOnly={<Icon.x size={14} />} onClick={() => rm(id)} aria-label={t("common.actions.remove", "Remove")} title={t("common.actions.remove", "Remove")} /> : null}
                </>}
              />
            </Card>
          );
        })}
        {canWrite ? <div><Btn variant="secondary" size="sm" icon={<Icon.plus size={13} />} onClick={() => setPicking(true)}>{t("services.delivery.addDiscounts", "Add discounts")}</Btn></div> : null}
      </div>
      <SearchPickerDialog
        open={picking}
        onClose={() => setPicking(false)}
        title={t("services.delivery.addDiscounts", "Add discounts")}
        searchPlaceholder={t("services.delivery.searchCouponsPromos", "Search coupons & promos…")}
        items={dstore.loading || dstore.error ? [] : pickable}
        emptyState={
          <div style={{ margin: T.space[4] }}>
            <CollectionState loading={dstore.loading} error={dstore.error}>
              <InlineEmptyState style={{ border: "none" }} icon={<IconTile size="lg" icon={<Icon.search size={20} />} />} title={t("services.delivery.noDiscountsMatch", "No discounts match.")} />
            </CollectionState>
          </div>
        }
        onPick={(id) => { add(id); setPicking(false); }}
      />
    </SvcRow>
  );
}

/* communications plan per method */
function MethodComms({ cfg, setComms, canWrite }: { cfg: SvcMethodConfig; setComms: (p: Record<string, any>) => void; canWrite: boolean }) {
  const t = useT();
  const comms = cfg.comms || { mode: "flow", flowId: "", custom: [] };
  const fstore = useFlows();
  const flows = fstore.all();
  const custom: { when: string; channel: string; subject: string }[] = comms.custom || [];
  const addMsg = () => setComms({ custom: [...custom, { when: "On booking", channel: "Email", subject: "" }] });
  const setMsg = (i: number, patch: Record<string, any>) => setComms({ custom: custom.map((m, idx) => idx === i ? { ...m, ...patch } : m) });
  const rmMsg = (i: number) => setComms({ custom: custom.filter((_, idx) => idx !== i) });
  return (
    <SvcRow label={t("services.delivery.communications", "Communications")} hint={t("services.delivery.hint11", "Messaging for this method — reuse a library flow or build a custom plan.")} full>
      <div style={{ marginTop: T.space[1] }}>
        <SvcSeg value={comms.mode} onChange={(v) => setComms({ mode: v })} disabled={!canWrite} options={[{ id: "flow", label: t("services.delivery.libraryFlow", "Library flow") }, { id: "custom", label: t("services.delivery.customPlan", "Custom plan") }]} />
        {comms.mode === "flow" ? (
          <div style={{ display: "flex", flexDirection: "column", gap: T.space[2], marginTop: T.space[3] }}>
            <CollectionState loading={fstore.loading} error={fstore.error}>
            {flows.length === 0 ? <InlineEmptyState size="sm" title={t("services.delivery.noFlowsInTheLibraryYet", "No flows in the library yet.")} /> : flows.map((f) => {
              const on = comms.flowId === f.id; const kind = FLOW_KIND_PILL[f.eventKind] || ({} as { label?: string });
              return (
                // `multiple` (check indicator), not the radio dot: picking the
                // selected flow again CLEARS it, which radio semantics swallow.
                <SelectableTile
                  key={f.id}
                  multiple
                  selected={on}
                  disabled={!canWrite}
                  onSelect={() => setComms({ flowId: on ? "" : f.id })}
                  title={f.name}
                  description={<>{t("services.delivery.stepCount.icu", "{count, plural, one {# step} other {# steps}}", { count: (f.steps || []).length })}{kind.label ? t("services.delivery.text3", "· {label}", { label: kind.label }) : ""}</>}
                />
              );
            })}
            </CollectionState>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: T.space[2], marginTop: T.space[3] }}>
            {custom.length === 0 ? <InlineEmptyState size="sm" title={t("services.delivery.noMessagesYet", "No messages yet.")} /> : custom.map((msg, i) => (
              <Card key={i} style={{ display: "flex", alignItems: "center", gap: T.space[2], padding: `${T.space[2]} ${T.space[3]}` }}>
                <div style={{ width: 140 }}><SvcSelect value={msg.when} onChange={(v) => setMsg(i, { when: v })} disabled={!canWrite} options={[{ id: "On booking", label: t("services.delivery.onBooking", "On booking") }, { id: "24h before", label: t("services.delivery.text24hBefore", "24h before") }, { id: "1h before", label: t("services.delivery.text1hBefore", "1h before") }, { id: "After service", label: t("services.delivery.afterService", "After service") }]} /></div>
                <div style={{ width: 120 }}><SvcSelect value={msg.channel} onChange={(v) => setMsg(i, { channel: v })} disabled={!canWrite} options={[{ id: "Email", label: t("services.delivery.email", "Email") }, { id: "SMS", label: t("services.delivery.sms", "SMS") }, { id: "WhatsApp", label: t("services.delivery.whatsapp", "WhatsApp") }]} /></div>
                <Input value={msg.subject} onChange={(e) => setMsg(i, { subject: e.target.value })} disabled={!canWrite} placeholder={t("services.delivery.subjectMessage", "Subject / message")} full={false} style={{ flex: 1, minWidth: 0 }} />
                {canWrite ? <Btn variant="ghost" size="sm" iconOnly={<Icon.x size={14} />} onClick={() => rmMsg(i)} aria-label={t("common.actions.remove", "Remove")} title={t("common.actions.remove", "Remove")} /> : null}
              </Card>
            ))}
            {canWrite ? <div><Btn variant="secondary" size="sm" icon={<Icon.plus size={13} />} onClick={addMsg}>{t("services.delivery.addMessage", "Add message")}</Btn></div> : null}
          </div>
        )}
      </div>
    </SvcRow>
  );
}
